import { useQuery } from "@tanstack/react-query";
import { Loader2, Play, CalendarCheck, TrendingUp, Eye, Film, Download } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const MONTH_LABEL = (mk) => {
  const [y, m] = mk.split("-");
  return new Date(Number(y), Number(m) - 1, 1).toLocaleString([], { month: "short" });
};

function MiniBars({ months, values, prefix = "", color = "#144F75", testid }) {
  const max = Math.max(1, ...values);
  return (
    <div className="flex items-end gap-2 h-28" data-testid={testid}>
      {values.map((v, i) => (
        <div key={i} className="flex-1 flex flex-col items-center gap-1">
          <span className="text-[10px] text-[#4A5D6B]">{prefix}{v % 1 === 0 ? v : v.toFixed(0)}</span>
          <div className="w-full rounded-t-md" style={{ height: `${(v / max) * 80}px`, minHeight: v > 0 ? 4 : 2, background: v > 0 ? color : "#E5E0D8" }} />
          <span className="text-[10px] text-[#8FA89B]">{MONTH_LABEL(months[i])}</span>
        </div>
      ))}
    </div>
  );
}

export default function CreatorAnalytics() {
  const { data, isLoading } = useQuery({
    queryKey: ["enh-analytics"],
    queryFn: () => api.get("/enhancer/me/analytics").then((r) => r.data),
  });

  if (isLoading) return <div className="grid place-items-center py-20"><Loader2 className="animate-spin text-[#144F75]" size={24} /></div>;
  const d = data || {};

  const exportCsv = async () => {
    try {
      const res = await api.get("/enhancer/me/analytics.csv", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "my-analytics.csv"; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Could not export right now."); }
  };

  return (
    <div data-testid="creator-analytics">
      <div className="flex items-start justify-between gap-3 mb-6">
        <div>
          <h2 className="font-serif text-2xl text-[#1A242B] mb-1">Your analytics</h2>
          <p className="text-sm text-[#4A5D6B]">A quick pulse on your videos, bookings and earnings.</p>
        </div>
        <button onClick={exportCsv} data-testid="analytics-export-btn" className="eyw-btn-ghost !py-2 text-sm shrink-0"><Download size={15} /> Export CSV</button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="eyw-card p-5" data-testid="analytics-plays"><Play size={18} className="text-[#144F75] mb-2" /><div className="text-3xl font-serif text-[#1A242B]">{d.total_plays ?? 0}</div><p className="text-xs text-[#4A5D6B] mt-1">Video plays</p></div>
        <div className="eyw-card p-5"><Film size={18} className="text-[#144F75] mb-2" /><div className="text-3xl font-serif text-[#1A242B]">{d.media_count ?? 0}</div><p className="text-xs text-[#4A5D6B] mt-1">Media items</p></div>
        <div className="eyw-card p-5"><CalendarCheck size={18} className="text-[#5C8A72] mb-2" /><div className="text-3xl font-serif text-[#1A242B]">{d.total_bookings ?? 0}</div><p className="text-xs text-[#4A5D6B] mt-1">Total bookings</p></div>
        <div className="eyw-card p-5"><TrendingUp size={18} className="text-[#D98E73] mb-2" /><div className="text-3xl font-serif text-[#1A242B]">{d.upcoming_bookings ?? 0}</div><p className="text-xs text-[#4A5D6B] mt-1">Upcoming</p></div>
      </div>

      <div className="grid md:grid-cols-2 gap-6 mb-8">
        <div className="eyw-card p-6">
          <h3 className="font-serif text-lg text-[#1A242B] mb-4">Bookings — last 6 months</h3>
          <MiniBars months={d.months || []} values={d.bookings_trend || []} color="#144F75" testid="bookings-chart" />
        </div>
        <div className="eyw-card p-6">
          <h3 className="font-serif text-lg text-[#1A242B] mb-4">Earnings — last 6 months</h3>
          <MiniBars months={d.months || []} values={d.earnings_trend || []} prefix="$" color="#5C8A72" testid="earnings-chart" />
        </div>
      </div>

      <div className="eyw-card p-6" data-testid="top-videos">
        <h3 className="font-serif text-lg text-[#1A242B] mb-4 flex items-center gap-2"><Eye size={18} /> Top media by views</h3>
        {(d.top_videos || []).length === 0 ? (
          <p className="text-sm text-[#8FA89B]">No media yet. Add videos or sync from YouTube to see plays here.</p>
        ) : (
          <div className="divide-y divide-[#E5E0D8]">
            {d.top_videos.map((v, i) => (
              <div key={i} className="flex items-center justify-between py-2.5">
                <span className="text-sm text-[#1A242B] truncate pr-3">{v.title}</span>
                <span className="text-sm text-[#144F75] font-medium shrink-0">{v.views} plays</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
